import { AlertTriangle, CheckCircle2, Info, RotateCw, XCircle } from 'lucide-react';
import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { cn } from '../cn';
import { Button } from './button';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-4', className)} aria-hidden />;
}

/** Explains what belongs here and offers the first action. */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}
    >
      {icon && (
        <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-rail text-ink-3 [&_svg]:size-6">
          {icon}
        </div>
      )}
      <p className="text-md font-semibold text-ink">{title}</p>
      {description && <p className="mt-1 max-w-sm text-base text-ink-3">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/**
 * Cashier-safe error: what happened in plain words and what to do next.
 * Never pass technical messages here.
 */
export function ErrorState({
  title = 'Не удалось выполнить операцию',
  description,
  onRetry,
  onContactAdmin,
  className,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  onContactAdmin?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}
    >
      <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-danger-soft text-danger">
        <AlertTriangle className="size-6" />
      </div>
      <p className="text-md font-semibold text-ink">{title}</p>
      {description && <p className="mt-1 max-w-sm text-base text-ink-3">{description}</p>}
      {(onRetry || onContactAdmin) && (
        <div className="mt-5 flex gap-2">
          {onRetry && (
            <Button variant="primary" icon={<RotateCw className="size-4" />} onClick={onRetry}>
              Повторить
            </Button>
          )}
          {onContactAdmin && <Button onClick={onContactAdmin}>Обратиться к администратору</Button>}
        </div>
      )}
    </div>
  );
}

/** Inline notice inside a page or form. */
export function Notice({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const styles = {
    info: 'bg-primary-soft text-primary-ink',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
  }[tone];
  const Icon = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle }[tone];
  return (
    <div
      className={cn('flex items-start gap-3 rounded-md px-4 py-3', styles, className)}
      // A danger notice is interrupting news — a screen reader should say it
      // now, not when it next gets a turn. Everything else stays polite.
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <Icon className="mt-0.5 size-5 shrink-0" />
      <div className="min-w-0 flex-1 text-base">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5', 'text-ink-2')}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

// ───────────────────────── Toasts ─────────────────────────

export type ToastTone = 'success' | 'danger' | 'info' | 'warning';
interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}

let toasts: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/**
 * Someone who wants to know a message went up — the register uses it to play
 * a sound per tone, so every error in every screen is heard without any
 * screen asking for it.
 */
const toastListeners = new Set<(tone: ToastTone) => void>();
export function onToast(listener: (tone: ToastTone) => void): () => void {
  toastListeners.add(listener);
  return () => toastListeners.delete(listener);
}

function push(tone: ToastTone, title: string, opts: Omit<ToastItem, 'id' | 'tone' | 'title'> = {}) {
  const item = { id: nextId++, tone, title, ...opts };
  toasts = [...toasts.slice(-3), item];
  emit();
  for (const l of toastListeners) l(tone);
  return item.id;
}

export const toast = {
  success: (title: string, opts?: Omit<ToastItem, 'id' | 'tone' | 'title'>) =>
    push('success', title, opts),
  error: (title: string, opts?: Omit<ToastItem, 'id' | 'tone' | 'title'>) =>
    push('danger', title, opts),
  info: (title: string, opts?: Omit<ToastItem, 'id' | 'tone' | 'title'>) =>
    push('info', title, opts),
  warning: (title: string, opts?: Omit<ToastItem, 'id' | 'tone' | 'title'>) =>
    push('warning', title, opts),
  dismiss: (id: number) => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  },
};

function ToastView({ item }: { item: ToastItem }) {
  useEffect(() => {
    const t = setTimeout(() => toast.dismiss(item.id), item.tone === 'danger' ? 8000 : 4000);
    return () => clearTimeout(t);
  }, [item.id, item.tone]);
  const Icon = { success: CheckCircle2, danger: XCircle, info: Info, warning: AlertTriangle }[
    item.tone
  ];
  const iconColor = {
    success: 'text-success',
    danger: 'text-danger',
    info: 'text-primary',
    warning: 'text-warning',
  }[item.tone];
  return (
    <div
      role={item.tone === 'danger' ? 'alert' : 'status'}
      className="pointer-events-auto flex w-96 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-lg border border-line bg-surface px-4 py-3 shadow-pop animate-toast-in"
    >
      <Icon className={cn('mt-0.5 size-5 shrink-0', iconColor)} />
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-ink">{item.title}</p>
        {item.description && <p className="mt-0.5 text-sm text-ink-3">{item.description}</p>}
      </div>
      {item.action && (
        <button
          type="button"
          onClick={() => {
            item.action?.onClick();
            toast.dismiss(item.id);
          }}
          className="shrink-0 rounded-sm px-2 py-1 text-sm font-semibold text-primary hover:bg-primary-soft"
        >
          {item.action.label}
        </button>
      )}
    </div>
  );
}

/** Mount once per app. */
export function Toaster({
  position = 'bottom-right',
  className,
}: {
  position?: 'bottom-right' | 'bottom-left' | 'top-center';
  /** Extra offsets, e.g. to clear a sidebar or an action strip. */
  className?: string;
}) {
  const items = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => toasts,
  );
  return (
    <div
      aria-live="polite"
      className={cn(
        'pointer-events-none fixed z-[60] flex flex-col gap-2',
        position === 'bottom-right' && 'right-4 bottom-4 items-end',
        position === 'bottom-left' && 'bottom-4 left-4 items-start',
        position === 'top-center' &&
          'top-[max(1rem,env(safe-area-inset-top))] left-1/2 -translate-x-1/2 items-center',
        className,
      )}
    >
      {items.map((t) => (
        <ToastView key={t.id} item={t} />
      ))}
    </div>
  );
}
